/**
 * Before-paint resolution of embedded-webview mode.
 *
 * The event and club detail pages are served from cache, so their HTML cannot
 * carry a per-visitor answer. This script runs synchronously while the document
 * is parsing, ahead of the header and footer, and marks the root element. CSS
 * keyed on that attribute hides the chrome that does not belong in the app's web
 * view, so the correct layout is there on the first paint rather than appearing
 * and then being removed.
 *
 * It is identical for every visitor, which is what keeps the HTML cacheable.
 * It also marks iPhone Safari, where Apple's Smart App Banner replaces the
 * in-page app promo.
 */

import {
  EMBEDDED_IN_APP_COOKIE,
  EMBEDDED_SESSION_TRANSFER_COOKIE,
} from "./embeddedInAppCookie";

/** Set on `<html>` while Discover runs inside the Spond app's web view. */
export const EMBEDDED_IN_APP_ATTRIBUTE = "data-embedded-in-app";

/** Set on `<html>` once /auth/sso has marked this web view. */
export const EMBEDDED_SESSION_TRANSFER_ATTRIBUTE =
  "data-embedded-session-transfer";

/** Set on `<html>` on iPhone Safari, the only browser that renders the Smart App Banner. */
export const SMART_APP_BANNER_ATTRIBUTE = "data-smart-app-banner";

function markIfCookieSet(cookie: string, attribute: string): string {
  return [
    `if(/(?:^|; *)${cookie}=true(?:;|$)/.test(document.cookie))`,
    `document.documentElement.setAttribute('${attribute}','');`,
  ].join("");
}

export function buildEmbeddedInAppBootstrapScript(): string {
  return [
    "try{",
    markIfCookieSet(EMBEDDED_IN_APP_COOKIE, EMBEDDED_IN_APP_ATTRIBUTE),
    markIfCookieSet(
      EMBEDDED_SESSION_TRANSFER_COOKIE,
      EMBEDDED_SESSION_TRANSFER_ATTRIBUTE,
    ),
    "var ua=navigator.userAgent;",
    "if(/iPhone/.test(ua)&&/Safari/.test(ua)&&!/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua))",
    `document.documentElement.setAttribute('${SMART_APP_BANNER_ATTRIBUTE}','');`,
    "}catch(e){}",
  ].join("");
}
